import classNames from "classnames";
import { graphql, Link } from "gatsby";
import Img from "gatsby-image";
import React from "react";
import CtaMini from "../components/CtaMini";
import WebsiteMeta from "../components/draftbox-meta/WebsiteMeta";
import Layout from "../components/Layout";
import Pagination from "../components/Pagination";
import PostCard from "../components/PostCard";
import { AllDraftboxPostCardData } from "../models/all-draftbox-post-description.model";
import { PaginationContext } from "../models/pagination.model";
import Company from "../components/Company";
import CoverLarge from "../components/CoverLarge";
import CoverMedium from "../components/CoverMedium";

type IndexPageProps = {
  data: {
    allDraftboxPost: AllDraftboxPostCardData;
    draftboxSettings: {
      identity: {
        siteName: string;
        tagLine: string;
        coverUrl: {
          publicURL: string;
          childImageSharp: {
            fluid: any;
          };
        };
      };
      content: {
        cta: {
          showAfterBlogfeed: boolean;
        };
        subscribe: {
          showAfterBlogfeed: boolean;
        };
        blogfeed: {
          newerPostsLabel: string;
          olderPostsLabel: string;
          blogCardBackgroundType: "firstLetter" | "icon";
          blogCardBackgroundColor: string;
          zeroPostsPlaceholderImage: {
            publicURL: string;
            childImageSharp: {
              fluid: any;
            };
          };
          zeroPostsPlacerholderText: string;
        };
        cover: {
          title: string;
          description: string;
          coverImage: {
            publicURL: string;
            childImageSharp: {
              fluid: any;
            };
          };
          visible: boolean;
          image: {
            publicURL: string;
            childImageSharp: {
              fluid: any;
            };
          };
          coverSize: "large" | "medium";
          contentAlignment: "left" | "center";
          textStyle: "light" | "dark";
          readabilityOverlay: boolean;
          navigation: Array<{
            label: string;
            type: "primary" | "outline" | "link";
            opensInNewTab;
            url: string;
          }>;
        };
      };
    };
  };
  location: any;
  pageContext?: PaginationContext;
};

const IndexPage: React.FC<IndexPageProps> = ({
  data,
  location,
  pageContext,
}) => {
  const {
    allDraftboxPost,
    draftboxSettings: {
      content: { cover, blogfeed, cta, subscribe },
    },
  } = data;

  return (
    <Layout>
      <WebsiteMeta />
      {cover.visible && cover.coverSize === "large" && (
        <CoverLarge
          title={cover.title}
          description={cover.description}
          coverSize="large"
          coverUrl={cover.coverImage}
          image={cover.image}
          textStyle={cover.textStyle}
          contentAlignment={cover.contentAlignment}
          readabilityOverlay={cover.readabilityOverlay}
          navigation={cover.navigation}
        />
      )}
      {cover.visible && cover.coverSize === "medium" && (
        <CoverMedium
          navigation={cover.navigation}
          title={cover.title}
          description={cover.description}
          coverUrl={cover.coverImage}
          image={cover.image}
          textStyle={cover.textStyle}
          contentAlignment={cover.contentAlignment}
          readabilityOverlay={cover.readabilityOverlay}
        />
      )}
      {/* <div className="spacer my-8"></div> */}
      <section className="px-6 container mx-auto mb-12 lg:mb-16">
        <div className="flex justify-center flex-wrap -mx-4">
          {allDraftboxPost &&
            allDraftboxPost.edges.length > 0 &&
            allDraftboxPost.edges.map(({ node }, index) => {
              return <PostCard post={node} index={index} key={index} />;
            })}
          {allDraftboxPost.edges.length === 0 && (
            <div className="flex flex-col items-center px-10">
              {blogfeed.zeroPostsPlaceholderImage && (
                <>
                  {!blogfeed?.zeroPostsPlaceholderImage?.childImageSharp &&
                    blogfeed?.zeroPostsPlaceholderImage?.publicURL && (
                      <img
                        className="w-full max-w-lg h-auto mt-16"
                        src={blogfeed.zeroPostsPlaceholderImage.publicURL}
                        alt=""
                      />
                    )}
                  {blogfeed?.zeroPostsPlaceholderImage?.childImageSharp && (
                    <div className="w-full max-w-lg h-auto overflow-hidden">
                      <Img
                        className="w-full h-auto mt-16"
                        fluid={
                          blogfeed.zeroPostsPlaceholderImage.childImageSharp
                            .fluid
                        }
                        alt=""
                      />
                    </div>
                  )}
                </>
              )}
              <div
                className="text-2xl mt-10 text-center max-w-3xl dangerous-html"
                dangerouslySetInnerHTML={{
                  __html: blogfeed.zeroPostsPlacerholderText,
                }}
              ></div>
            </div>
          )}
        </div>
      </section>

      {/* Pagination */}
      <Pagination pageContext={pageContext} />
      {subscribe.showAfterBlogfeed && <CtaMini />}
      {cta.showAfterBlogfeed && (
        <Company size="large" className="my-12 lg:my-16 container mx-auto" />
      )}
    </Layout>
  );
};

export default IndexPage;

export const indexPageQuery = graphql`
  query IndexPageQuery($limit: Int!, $skip: Int!) {
    allDraftboxPost(
      sort: { fields: [featured, publishedAt], order: [DESC, DESC] }
      limit: $limit
      skip: $skip
    ) {
      edges {
        node {
          ...DraftboxPostCardData
        }
      }
    }

    draftboxSettings {
      identity {
        coverUrl {
          publicURL
          childImageSharp {
            fluid {
              ...GatsbyImageSharpFluid_withWebp_tracedSVG
            }
          }
        }
      }
      content {
        blogfeed {
          newerPostsLabel
          olderPostsLabel
          blogCardBackgroundType
          blogCardBackgroundColor
          zeroPostsPlaceholderImage {
            publicURL
            childImageSharp {
              fluid {
                ...GatsbyImageSharpFluid_withWebp_tracedSVG
              }
            }
          }
          zeroPostsPlacerholderText
        }
        subscribe {
          showAfterBlogfeed
        }
        cta {
          showAfterBlogfeed
        }
        cover {
          title
          description
          coverImage {
            publicURL
            childImageSharp {
              fluid(maxWidth: 1920, quality: 90) {
                ...GatsbyImageSharpFluid_withWebp_tracedSVG
              }
            }
          }
          visible
          image {
            publicURL
            childImageSharp {
              fluid {
                ...GatsbyImageSharpFluid_withWebp_tracedSVG
              }
            }
          }
          coverSize
          contentAlignment
          textStyle
          readabilityOverlay
          navigation {
            type
            opensInNewTab
            label
            url
          }
        }
      }
    }
  }
`;
